import { gql } from '@apollo/client';

export const ME_QUERY = gql`
  query Me {
    me {
      id
      firstName
      lastName
      email
      companyName
      industry
      role
      adminModules
      isSubuser
      account { id firstName lastName email companyName }
      avatar
      lastLoginAt
      sendEmailsTo
      permissions {
        sceneVisible
        matterportVisible
        releaseNotesVisible
        gaussianVisible
        gaussianGenerate
        orthomosaicVisible
        lgsxUpload
        video360Visible
        basemapVisible
      }
      planName
      accountStorageLimit
      accountStorageUsed
      downloadsExceeded
      trialEndsAt
      userPlan {
        id
        name
        accountStorage
        filesDownloadPerMonth
        downloadGbPerMonth
      }
    }
  }
`;

export const ADMIN_ALL_USER_PLANS_QUERY = gql`
  query AdminAllUserPlans {
    adminAllUserPlans {
      id
      name
      accountStorage
      filesDownloadPerMonth
      downloadGbPerMonth
      schedulingPriority
      gaussianPerMonth
      limitedTime
      limitedTimeDays
      allowSelectForUpgrade
      maxSubusers
      userCount
      createdAt
      updatedAt
    }
  }
`;

export const DASHBOARD_STATS_QUERY = gql`
  query DashboardStats {
    dashboardStats {
      totalProjects
      totalFiles
      storageUsed
      totalMembers
      recentProjects {
        id
        name
        location
        latitude
        longitude
        status
        storageUsed
        fileCount
        updatedAt
        owner {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const FILE_FOLDERS_QUERY = gql`
  query FileFolders($projectId: ID!, $fileType: FileType!) {
    fileFolders(projectId: $projectId, fileType: $fileType) {
      id
      name
      fileType
      createdAt
    }
  }
`;

export const PROJECTS_QUERY = gql`
  query Projects($page: Int, $limit: Int, $search: String, $status: ProjectStatus) {
    projects(page: $page, limit: $limit, search: $search, status: $status) {
      projects {
        id
        name
        description
        location
        latitude
        longitude
        status
        thumbnail
        thumbnailUrl
        storageUsed
        fileCount
        updatedAt
        createdAt
        owner {
          id
          firstName
          lastName
          email
          avatar
        }
        members {
          user {
            id
            firstName
            lastName
            email
            avatar
          }
          role
          joinedAt
        }
      }
      total
      page
      limit
    }
  }
`;

export const PROJECT_QUERY = gql`
  query Project($id: ID!) {
    project(id: $id) {
      id
      name
      description
      location
      latitude
      longitude
      epsg
      matterportUrl
      overviewPanel
      status
      thumbnail
      thumbnailUrl
      storageUsed
      fileCount
      updatedAt
      createdAt
      owner {
        id
        firstName
        lastName
        email
        avatar
      }
      members {
        user {
          id
          firstName
          lastName
          email
          avatar
        }
        role
        joinedAt
      }
      pendingTransfer {
        id
        toEmail
        status
        createdAt
      }
    }
  }
`;

export const PROJECT_TRANSFER_INFO_QUERY = gql`
  query ProjectTransferInfo($token: String!) {
    projectTransferInfo(token: $token) {
      token
      status
      projectId
      projectName
      projectDescription
      fromName
      toEmail
      recipientExists
      storageUsed
      fileCount
      sections {
        type
        label
        count
        size
      }
    }
  }
`;


export const PROJECT_FILES_QUERY = gql`
  query ProjectFiles($projectId: ID!, $type: FileType, $page: Int, $limit: Int, $search: String, $folderId: ID) {
    projectFiles(projectId: $projectId, type: $type, page: $page, limit: $limit, search: $search, folderId: $folderId) {
      files {
        id
        fileName
        originalName
        filePath
        fileSize
        mimeType
        type
        extension
        status
        folderId
        panoramaCount
        thumbnailUrl
        video360Path
        videoDuration
        sceneTransform { x y z rot scale }
        srs { epsg proj4 projected }
        archiveState
        restoreScope
        restoreExpiresAt
        createdAt
        uploadedBy {
          id
          firstName
          lastName
          avatar
        }
      }
      total
      page
      limit
    }
  }
`;

export const SHARED_LINKS_QUERY = gql`
  query SharedLinks($projectId: ID, $page: Int, $limit: Int, $search: String) {
    sharedLinks(projectId: $projectId, page: $page, limit: $limit, search: $search) {
      links {
        id
        token
        permission
        expiresAt
        status
        viewCount
        maxViews
        recipientEmail
        note
        allowDownload
        allowScene
        passwordProtected
        createdAt
        project {
          id
          name
        }
        file {
          id
          originalName
          type
        }
        createdBy {
          id
          firstName
          lastName
        }
      }
      total
      page
      limit
      stats { active expired revoked views }
    }
  }
`;

export const LOGS_QUERY = gql`
  query Logs($projectId: ID, $userId: ID, $action: LogAction, $actions: [LogAction!], $page: Int, $limit: Int, $search: String) {
    logs(projectId: $projectId, userId: $userId, action: $action, actions: $actions, page: $page, limit: $limit, search: $search) {
      logs {
        id
        action
        description
        ipAddress
        createdAt
        user {
          id
          firstName
          lastName
          avatar
        }
        project {
          id
          name
        }
        file {
          id
          originalName
        }
      }
      total
      page
      limit
    }
  }
`;

export const TEAM_MEMBERS_QUERY = gql`
  query TeamMembers($projectId: ID!) {
    teamMembers(projectId: $projectId) {
      members {
        user {
          id
          firstName
          lastName
          email
          avatar
          role
          lastLoginAt
        }
        role
        joinedAt
      }
      total
    }
  }
`;

export const ALL_USERS_QUERY = gql`
  query AllUsers($search: String) {
    allUsers(search: $search) {
      id
      firstName
      lastName
      email
      avatar
      role
    }
  }
`;

export const ADMIN_STATS_QUERY = gql`
  query AdminStats {
    adminStats {
      totalUsers
      activeUsers
      totalProjects
      totalFiles
      totalStorage
    }
  }
`;

export const ADMIN_ALL_PROJECTS_QUERY = gql`
  query AdminAllProjects(
    $page: Int
    $limit: Int
    $search: String
    $status: ProjectStatus
    $ownerId: ID
    $sortBy: String
    $sortDir: String
    $onlyDeleted: Boolean
  ) {
    adminAllProjects(
      page: $page
      limit: $limit
      search: $search
      status: $status
      ownerId: $ownerId
      sortBy: $sortBy
      sortDir: $sortDir
      onlyDeleted: $onlyDeleted
    ) {
      projects {
        id
        name
        description
        location
        status
        thumbnailUrl
        storageUsed
        fileCount
        updatedAt
        createdAt
        deletedAt
        openLogLimit
        openLogs {
          email
          ip
          at
        }
        owner {
          id
          firstName
          lastName
          email
        }
      }
      total
      page
      limit
    }
  }
`;

// A project's team for the admin dialog — the owner plus every member.
export const ADMIN_PROJECT_MEMBERS_QUERY = gql`
  query AdminProjectMembers($projectId: ID!) {
    adminProjectMembers(projectId: $projectId) {
      projectId
      name
      owner {
        id
        firstName
        lastName
        email
      }
      members {
        role
        joinedAt
        user {
          id
          firstName
          lastName
          email
          companyName
        }
      }
    }
  }
`;

export const ADMIN_ALL_USERS_QUERY = gql`
  query AdminAllUsers($page: Int, $limit: Int, $search: String, $role: String, $account: String, $parentUserId: ID) {
    adminAllUsers(page: $page, limit: $limit, search: $search, role: $role, account: $account, parentUserId: $parentUserId) {
      users {
        id
        firstName
        lastName
        email
        companyName
        industry
        role
        adminModules
        isSubuser
        subuserStatus
        subuserCount
        parentUser { id email firstName lastName }
        isActive
        lastLoginAt
        createdAt
        permissions {
          sceneVisible
          matterportVisible
          releaseNotesVisible
          gaussianVisible
          gaussianGenerate
          lgsxUpload
          orthomosaicVisible
          video360Visible
          basemapVisible
        }
        userPlan {
          id
          name
        }
      }
      total
      page
      limit
    }
  }
`;

export const ADMIN_CONVERSION_JOBS_QUERY = gql`
  query AdminConversionJobs($page: Int, $limit: Int, $status: String, $fileType: FileType, $search: String) {
    adminConversionJobs(page: $page, limit: $limit, status: $status, fileType: $fileType, search: $search) {
      jobs {
        id
        originalName
        extension
        fileSize
        type
        status
        apsStatus
        apsUrn
        svStatus
        potreePath
        batchJobId
        batchJobName
        errorMessage
        project {
          id
          name
        }
        uploadedBy {
          id
          firstName
          lastName
        }
        createdAt
        updatedAt
      }
      total
      page
      limit
    }
  }
`;

export const ADMIN_DELETED_FILES_QUERY = gql`
  query AdminDeletedFiles($page: Int, $limit: Int, $search: String) {
    adminDeletedFiles(page: $page, limit: $limit, search: $search) {
      jobs {
        id
        originalName
        extension
        fileSize
        type
        status
        deletedAt
        deletedWithProject
        deletedBy { id firstName lastName email }
        removesAt
        removing
        purgedAt
        project { id name }
        uploadedBy { id firstName lastName }
        createdAt
        updatedAt
      }
      total
      page
      limit
    }
  }
`;

export const PASSWORD_RESET_TOKEN_VALID_QUERY = gql`
  query PasswordResetTokenValid($token: String!) {
    passwordResetTokenValid(token: $token)
  }
`;

export const MY_TRASH_QUERY = gql`
  query MyTrash($page: Int, $limit: Int, $search: String) {
    myTrash(page: $page, limit: $limit, search: $search) {
      items {
        id
        kind
        name
        fileType
        projectId
        projectName
        bytes
        fileCount
        deletedAt
        deletedBy
        removesAt
        removing
      }
      total
      page
      limit
      totalBytes
      nextRemovalAt
      retentionDays
      allowImmediateDelete
      immediateDeleteGbPerMonth
      immediateDeleteUsedGb
      immediateDeleteLeftGb
    }
  }
`;

export const TRASH_SETTINGS_SELECTION = 'retentionDays allowImmediateDelete immediateDeleteGbPerMonth';

export const ADMIN_TRASH_SETTINGS_QUERY = gql`
  query AdminTrashSettings {
    adminTrashSettings { ${TRASH_SETTINGS_SELECTION} }
  }
`;

export const SCENE_SETTINGS_SELECTION = 'walkSpeedMps runMultiplier distanceFactor';

export const FILE_FORMATS_QUERY = gql`
  query FileFormats {
    fileFormats { documents { extension viewer } }
  }
`;

export const SCENE_SETTINGS_QUERY = gql`
  query SceneSettings {
    sceneSettings { ${SCENE_SETTINGS_SELECTION} }
  }
`;

export const ADMIN_TRASH_OVERVIEW_QUERY = gql`
  query AdminTrashOverview($retentionDays: Int) {
    adminTrashOverview(retentionDays: $retentionDays) {
      files
      projects
      bytes
      dueFiles
      dueProjects
      dueBytes
    }
  }
`;

export const SCHEDULED_TASK_SELECTION = `
  key name group description enabled intervalSeconds defaultIntervalSeconds minIntervalSeconds
  unavailableReason running pending nextRunAt lastStartedAt lastFinishedAt lastDurationMs
  lastStatus lastMessage lastError lastTrigger runCount failCount updatedAt
`;

export const ADMIN_SCHEDULED_TASKS_QUERY = gql`
  query AdminScheduledTasks {
    adminScheduledTasks { ${SCHEDULED_TASK_SELECTION} }
  }
`;

export const ADMIN_PROJECTS_QUERY = gql`
  query AdminProjects($search: String, $limit: Int) {
    adminProjects(search: $search, limit: $limit) {
      id
      name
      location
    }
  }
`;

export const SHARED_LINK_QUERY = gql`
  query SharedLink($token: String!) {
    sharedLink(token: $token) {
      id
      token
      permission
      expiresAt
      status
      viewCount
      maxViews
      note
      allowDownload
      allowScene
      allowPointCloudMeasurements
      allowOrthoMeasurements
      ownerAccess { scene splat ortho video360 matterport basemap }
      createdAt
      project {
        id
        name
        description
        location
        latitude
        longitude
        epsg
        matterportUrl
        basemap { provider opacity terrain height position gridAdjust { dx dy rotation pivotX pivotY } }
        sitePlacement { lat lng heading originX originY originZ }
      }
      files {
        id
        originalName
        type
        fileSize
        extension
        status
        apsUrn
        apsStatus
        panoramaCount
        sceneTransform { x y z rot scale }
        srs { epsg proj4 projected }
      }
      createdBy {
        firstName
        lastName
      }
    }
  }
`;

export const PLATFORM_SETTINGS_QUERY = gql`
  query PlatformSettings {
    platformSettings {
      usersAllowedToRegister
      defaultUserPlan {
        id
        name
      }
    }
  }
`;

// Platform Settings → User Registration: the public settings plus the switches
// a new registration starts with (null for anyone not managing Platform Settings).
export const ADMIN_PLATFORM_SETTINGS_QUERY = gql`
  query AdminPlatformSettings {
    platformSettings {
      usersAllowedToRegister
      defaultUserPlan {
        id
        name
      }
      newUserPermissions { sceneVisible lgsxUpload matterportVisible releaseNotesVisible gaussianVisible gaussianGenerate orthomosaicVisible video360Visible basemapVisible }
    }
  }
`;

export const FILE_ANNOTATIONS_QUERY = gql`
  query FileAnnotations($fileId: ID!) {
    fileAnnotations(fileId: $fileId) {
      id
      fileId
      createdBy {
        id
        firstName
        lastName
      }
      position { x y z }
      text
      status
      guestName
      replies {
        id
        createdBy {
          id
          firstName
          lastName
        }
        guestName
        text
        createdAt
      }
      attachments {
        id
        fileName
        mimeType
        fileSize
        createdAt
        url
        downloadUrl
        uploadedBy {
          id
          firstName
          lastName
        }
      }
      linkedFiles {
        id
        originalName
        type
        extension
        status
      }
      createdAt
      updatedAt
    }
  }
`;

// Whether a share-link visitor may add notes on this file (the link's
// per-viewer "allow adding notes" flags).
export const SHARED_NOTES_ACCESS_QUERY = gql`
  query SharedNotesAccess($token: String!, $fileId: ID!) {
    sharedNotesAccess(token: $token, fileId: $fileId)
  }
`;

// Public, read-only notes for /share/:token viewers (no account).
export const SHARED_FILE_ANNOTATIONS_QUERY = gql`
  query SharedFileAnnotations($token: String!, $fileId: ID!) {
    sharedFileAnnotations(token: $token, fileId: $fileId) {
      id
      fileId
      createdBy {
        id
        firstName
        lastName
      }
      position { x y z }
      text
      status
      guestName
      replies {
        id
        createdBy {
          id
          firstName
          lastName
        }
        guestName
        text
        createdAt
      }
      attachments {
        id
        fileName
        mimeType
        fileSize
        createdAt
        url
        downloadUrl
        uploadedBy {
          id
          firstName
          lastName
        }
      }
      linkedFiles {
        id
        originalName
        type
        extension
        status
      }
      createdAt
      updatedAt
    }
  }
`;

export const FILE_MEASUREMENTS_QUERY = gql`
  query FileMeasurements($fileId: ID!) {
    fileMeasurements(fileId: $fileId) {
      id
      kind
      name
      layout
      createdBy { id firstName lastName }
      guestName
      start { x y z }
      end { x y z }
      distance
      points { x y z }
      area
      createdAt
    }
  }
`;

// Saved measurements for /share/:token viewers (no account) — every link
// shows them; fromThisLink marks the ones a guest may delete.
export const SHARED_FILE_MEASUREMENTS_QUERY = gql`
  query SharedFileMeasurements($token: String!, $fileId: ID!) {
    sharedFileMeasurements(token: $token, fileId: $fileId) {
      id
      kind
      name
      layout
      createdBy { id firstName lastName }
      guestName
      fromThisLink
      start { x y z }
      end { x y z }
      distance
      points { x y z }
      area
      createdAt
    }
  }
`;

export const FILE_FOCUS_ZONES_QUERY = gql`
  query FileFocusZones($fileId: ID!) {
    fileFocusZones(fileId: $fileId) {
      id
      name
      min { x y z }
      max { x y z }
      createdBy { id firstName lastName }
      createdAt
    }
  }
`;


export const ADMIN_CONVERSION_ALERTS_QUERY = gql`
  query AdminConversionAlerts {
    adminConversionAlerts {
      severity
      code
      message
      lane
      metricId
      since
    }
  }
`;

export const ADMIN_CONVERSION_STATS_QUERY = gql`
  query AdminConversionStats($rangeDays: Int) {
    adminConversionStats(rangeDays: $rangeDays) {
      rangeDays
      totalJobs
      succeeded
      failed
      runningNow
      queuedNow
      successRate
      p50QueueSeconds
      p95QueueSeconds
      maxQueueSeconds
      p50CrossUserWaitSeconds
      p95CrossUserWaitSeconds
      maxCrossUserWaitSeconds
      p50RunSeconds
      p95RunSeconds
      lanes {
        lane
        capacity
        runningNow
        queuedNow
        longestCurrentWaitSeconds
        jobs
        failed
        avgQueueSeconds
        p50QueueSeconds
        p95QueueSeconds
        maxQueueSeconds
        p50CrossUserWaitSeconds
        p95CrossUserWaitSeconds
        maxCrossUserWaitSeconds
        avgRunSeconds
        p50RunSeconds
        p95RunSeconds
        maxRunSeconds
      }
      modes {
        mode
        jobs
        failed
        p50QueueSeconds
        p95QueueSeconds
        p50RunSeconds
        p95RunSeconds
        avgRunSeconds
      }
      daily {
        date
        jobs
        failed
        heavy
        standard
        light
        gpu
        p50QueueSeconds
        p95QueueSeconds
        p50RunSeconds
        p95RunSeconds
      }
      slowestWaits {
        id
        jobName
        fileName
        lane
        mode
        queueSeconds
        crossUserWaitSeconds
        submittedAt
        user {
          id
          firstName
          lastName
          email
        }
      }
      topWaitingUsers {
        user {
          id
          firstName
          lastName
          email
        }
        jobs
        totalCrossUserWaitSeconds
        maxCrossUserWaitSeconds
      }
    }
  }
`;

export const ADMIN_CONVERSION_METRICS_QUERY = gql`
  query AdminConversionMetrics($page: Int, $limit: Int, $lane: String, $mode: String, $status: String, $search: String, $sinceDays: Int) {
    adminConversionMetrics(page: $page, limit: $limit, lane: $lane, mode: $mode, status: $status, search: $search, sinceDays: $sinceDays) {
      metrics {
        id
        jobId
        jobName
        mode
        lane
        status
        batchStatus
        statusReason
        exitCode
        errorMessage
        fileId
        fileName
        fileExt
        fileSizeBytes
        user {
          id
          firstName
          lastName
          email
        }
        project {
          id
          name
        }
        schedulingPriority
        submittedAt
        startedAt
        stoppedAt
        queueSeconds
        runSeconds
        crossUserWaitSeconds
      }
      total
      page
      limit
    }
  }
`;

export const ADMIN_USER_ACTIVITY_REPORT_QUERY = gql`
  query AdminUserActivityReport($rangeDays: Int) {
    adminUserActivityReport(rangeDays: $rangeDays) {
      rangeDays
      totalEvents
      activeUsers
      actions {
        action
        count
        uniqueUsers
      }
    }
  }
`;

export const ADMIN_USER_ACTIVITY_LEADERBOARD_QUERY = gql`
  query AdminUserActivityLeaderboard(
    $action: String, $rangeDays: Int, $minCount: Int, $page: Int, $limit: Int, $search: String,
    $sortBy: String, $sortDir: String, $lastActiveFrom: DateTime, $lastActiveBefore: DateTime
  ) {
    adminUserActivityLeaderboard(
      action: $action, rangeDays: $rangeDays, minCount: $minCount, page: $page, limit: $limit, search: $search,
      sortBy: $sortBy, sortDir: $sortDir, lastActiveFrom: $lastActiveFrom, lastActiveBefore: $lastActiveBefore
    ) {
      rows {
        user {
          id
          firstName
          lastName
          email
          companyName
          role
          isActive
        }
        count
        lastActiveAt
      }
      total
      page
      limit
    }
  }
`;

export const ADMIN_USER_ACTIVITY_DETAIL_QUERY = gql`
  query AdminUserActivityDetail($userId: ID!, $rangeDays: Int) {
    adminUserActivityDetail(userId: $userId, rangeDays: $rangeDays) {
      action
      count
    }
  }
`;

export const RELEASE_NOTES_QUERY = gql`
  query ReleaseNotes($page: Int, $limit: Int) {
    releaseNotes(page: $page, limit: $limit) {
      notes {
        id
        version
        releaseDate
        shareToken
        items {
          title
        }
      }
      total
      page
      limit
    }
  }
`;

export const RELEASE_NOTE_QUERY = gql`
  query ReleaseNote($version: String!) {
    releaseNote(version: $version) {
      id
      version
      releaseDate
      items {
        title
        description
        files {
          s3Key
          fileName
          mimeType
          fileSize
          url
        }
      }
    }
  }
`;

export const SHARED_RELEASE_NOTE_QUERY = gql`
  query SharedReleaseNote($token: String!) {
    sharedReleaseNote(token: $token) {
      id
      version
      releaseDate
      items {
        title
        description
        files {
          s3Key
          fileName
          mimeType
          fileSize
          url
        }
      }
    }
  }
`;

export const GAUSSIAN_QUOTA_QUERY = gql`
  query GaussianQuota {
    gaussianQuota {
      used
      limit
    }
  }
`;

// Modelled costs from our own job/storage metrics × the admin's unit prices.
// Every unit price, in the order the admin page lists them.
export const COST_RATE_SELECTION = `laneHeavyUsdPerHour laneStandardUsdPerHour laneLightUsdPerHour laneGpuUsdPerHour
  s3UsdPerGbMonth s3ArchiveUsdPerGbMonth s3InfrequentUsdPerGbMonth s3InstantArchiveUsdPerGbMonth s3TieringUsdPer1000Objects
  storageFactorPointCloud storageFactorGaussian storageFactor2d storageFactor3d storageFactorOrthomosaic storageFactorVideo360
  intermediateKeepDays cloudfrontUsdPerGb cloudfrontUsdPer10kRequests egressUsdPerGb
  viewerGbPerView viewerRequestsPerView splatGbPerView splatRequestsPerView video360GbPerView video360RequestsPerView
  drawingGbPerView modelGbPerView orthoGbPerView drawingRequestsPerView modelRequestsPerView orthoRequestsPerView fixedMonthlyUsd
  machineHeavyUsdPerHour machineStandardUsdPerHour machineLightUsdPerHour machineGpuUsdPerHour
  mapTilesEsriUsdPer1000 mapTilesEsriFreePerMonth mapTilesGoogleUsdPer1000 mapTilesGoogleFreePerMonth cdnPlan
  cdnPlanSince cdnPreviousPlan`;

// One calculator result: the admin page and a calculator share link ask for
// the same fields (on a link the server leaves the usage evidence empty).
const COST_FORECAST_SELECTION = `
      months
      users
      viewDecayPct
      rates { ${COST_RATE_SELECTION} }
      lines {
        id type label files avgSizeMb conversionsPerFile lane totalGb jobs runHours computeUsd
        storageFactor storageMonthlyUsd storageUsd downloadsPerFile viewsPerFile viewDelivery downloadDelivery
        downloads downloadGb downloadUsd views viewGb viewUsd transferUsd totalUsd firstMonthUsd avgMonthlyUsd perFileUsd
        steps { label lane jobs runHours computeUsd hoursPerJob samples source machineHours costSource }
      }
      monthly {
        month newFiles storedFiles storedGb computeUsd downloadUsd storageUsd storageHotGb storageArchiveGb
        storageHotUsd storageArchiveUsd storageSavedUsd storageFrequentGb storageInfrequentGb storageInstantGb
        cdnDownloadGb views viewRequests viewGb viewUsd fixedUsd totalUsd
      }
      archive { enabled delayedDownloadDays fullArchiveDays stage1Months stage2Months savedUsd }
      delivery { intelligentTiering downloadsViaCloudfront maxCdnDownloadGb originalsReadPct tieringSavedUsd monitoringUsd }
      intake {
        pattern label weights quietPct swingPct peakMonth startMonth rampStartPct rampMonths burstMonths
        burstEvery storageCapGb deleteAfterMonths
      }
      range {
        quietPct monthlyLow monthlyHigh
        low { totalUsd firstMonthUsd lastMonthUsd avgMonthlyUsd perUserUsd perUserMonthlyUsd }
        high { totalUsd firstMonthUsd lastMonthUsd avgMonthlyUsd perUserUsd perUserMonthlyUsd }
      }
      totals {
        files monthlyFiles jobs totalGb monthlyGb runHours computeUsd monthlyComputeUsd storageMonthlyUsd
        storageUsd fixedMonthlyUsd fixedUsd downloads downloadGb downloadUsd views viewGb viewUsd
        viewMonthlyGrowthUsd transferUsd firstMonthUsd lastMonthUsd avgMonthlyUsd totalUsd perFileUsd
        perGbUsd perUserUsd perUserMonthlyUsd
      }
      models { key label lane samples overheadHours hoursPerGb medianGb source }
      cdn {
        plan planLabel perViewUsd peakMonth peakViews peakRequests peakGb peakDownloads peakDownloadGb
        requestShare gbShare firstOverMonth pricedPlan pricedPlanLabel
        recommended recommendedLabel
        options {
          id label usdMonthly requests gb s3CreditGb monthlyUsd s3CreditUsd netUsd
          requestShare gbShare fits sessions
        }
      }
      laneModels { lane samples machines startupHours busyPerRunHour jobsPerStart source }
      storageMultipliers { type label multiplier }
      viewerMeasured { sessions requests gb minutes }
`;

export const ADMIN_COST_FORECAST_QUERY = gql`
  query AdminCostForecast($input: CostForecastInput!) {
    adminCostForecast(input: $input) {${COST_FORECAST_SELECTION}}
  }
`;

export const SHARED_COST_FORECAST_QUERY = gql`
  query SharedCostForecast($token: String!, $input: CostForecastInput!) {
    sharedCostForecast(token: $token, input: $input) {${COST_FORECAST_SELECTION}}
  }
`;

export const SHARED_CALCULATOR_QUERY = gql`
  query SharedCalculator($token: String!) {
    sharedCalculator(token: $token) { valid status label expiresAt }
  }
`;

export const ADMIN_COST_ANALYSIS_QUERY = gql`
  query AdminCostAnalysis($rangeDays: Int, $startDate: String, $endDate: String) {
    adminCostAnalysis(rangeDays: $rangeDays, startDate: $startDate, endDate: $endDate) {
      rangeDays
      since
      generatedAt
      rates { ${COST_RATE_SELECTION} }
      totals {
        jobs
        failedJobs
        runHours
        machineHours
        measuredJobs
        computeUsd
        storageGb
        storageUsd
        storageCoverage
        archiveEnabled
        archivedGb
        archiveSavedUsd
        tieringEnabled
        infrequentGb
        instantGb
        tieringSavedUsd
        tieringMonitoringUsd
        fixedUsd
        downloads
        downloadGb
        downloadUsd
        cdnDownloads
        cdnDownloadGb
        cdnDownloadUsd
        shareDownloads
        shareDownloadGb
        docViews
        docViewGb
        docViewUsd
        views
        shareViews
        reportedViews
        viewHours
        viewGb
        viewRequests
        viewUsd
        viewS3Gb
        viewS3Usd
        viewProxyGb
        viewProxyUsd
        mapTilesEsri
        mapTilesGoogle
        mapTilesOsm
        mapTilesUsd
        transferUsd
        cdnCreditUsd
        totalUsd
        gbProcessed
        activeUsers
        billedUsers
      }
      byCategory { key label jobs runHours machineHours computeUsd gbProcessed avgPerJobUsd avgPerGbUsd }
      byLane { key label jobs runHours machineHours computeUsd gbProcessed avgPerJobUsd avgPerGbUsd }
      averages { perUserUsd perActiveUserUsd perConversionUsd perGbProcessedUsd perStoredGbUsd perDownloadUsd }
      cdn {
        plan
        planLabel
        views
        reportedSessions
        estimatedSessions
        measuredRequests
        measuredGb
        perSessionRequests
        perSessionGb
        perSessionSource
        requests
        gb
        monthlyRequests
        monthlyGb
        requestShare
        gbShare
        fits
        usd
        s3CreditUsd
        perViewUsd
        recommended
        recommendedLabel
        options {
          id label usdMonthly requests gb s3CreditGb monthlyUsd s3CreditUsd netUsd
          requestShare gbShare fits sessions
        }
        downloads
        downloadGb
        downloadUsd
        planSince
        previousPlan
        previousPlanLabel
        previousPlanDays
      }
      delivery { intelligentTiering intelligentTieringSince downloadsViaCloudfront cloudfrontConfigured maxCdnDownloadGb }
      archive { enabled delayedDownloadDays fullArchiveDays restoreKeepDays }
      measurement {
        compute { jobs measuredJobs runHours machineHours lookupError }
        storage {
          coverage recordedGb estimatedGb folders recordedFiles estimatedFiles lastRecordedAt
          endAt endGb endRecordedGb endEstimatedGb
          byKind { key label gb }
        }
        viewing {
          reportedSessions estimatedSessions perSessionRequests perSessionGb perSessionSource
          perSessionSamples viewHours avgVisibleMinutes
        }
      }
    }
  }
`;

// One user's cost breakdown, with platform averages to compare against.
// The cost-per-user table, a page at a time (search: name, email or company).
export const ADMIN_COST_USERS_QUERY = gql`
  query AdminCostUsers($rangeDays: Int, $startDate: String, $endDate: String, $page: Int, $limit: Int, $search: String) {
    adminCostUsers(rangeDays: $rangeDays, startDate: $startDate, endDate: $endDate, page: $page, limit: $limit, search: $search) {
      users {
        userId
        name
        email
        company
        subusers
        jobs
        failedJobs
        runHours
        computeUsd
        gbProcessed
        storageGb
        storageUsd
        downloads
        downloadGb
        shareDownloads
        views
        transferUsd
        totalUsd
        avgPerConversionUsd
        avgPerGbUsd
      }
      total
      page
      limit
    }
  }
`;

export const ADMIN_USER_COST_DETAIL_QUERY = gql`
  query AdminUserCostDetail($userId: ID!, $rangeDays: Int, $startDate: String, $endDate: String) {
    adminUserCostDetail(userId: $userId, rangeDays: $rangeDays, startDate: $startDate, endDate: $endDate) {
      rangeDays
      userId
      name
      email
      company
      memberSince
      totals {
        jobs
        failedJobs
        runHours
        machineHours
        measuredJobs
        computeUsd
        storageGb
        storageUsd
        storageCoverage
        archiveEnabled
        archivedGb
        archiveSavedUsd
        tieringEnabled
        infrequentGb
        instantGb
        tieringSavedUsd
        downloads
        downloadGb
        downloadUsd
        cdnDownloads
        cdnDownloadGb
        cdnDownloadUsd
        shareDownloads
        views
        shareViews
        reportedViews
        viewHours
        viewGb
        viewUsd
        viewS3Gb
        viewS3Usd
        viewProxyGb
        viewProxyUsd
        mapTilesUsd
        transferUsd
        totalUsd
        gbProcessed
      }
      averages { perConversionUsd perGbProcessedUsd perStoredGbUsd perDownloadUsd }
      platformAverages { perUserUsd perConversionUsd perGbProcessedUsd perStoredGbUsd perDownloadUsd }
      byCategory { key label jobs runHours machineHours computeUsd gbProcessed avgPerJobUsd avgPerGbUsd }
      byLane { key label jobs runHours machineHours computeUsd gbProcessed avgPerJobUsd avgPerGbUsd }
      byProject { projectId name jobs computeUsd storageGb storageUsd totalUsd }
      daily { date jobs computeUsd gbProcessed transferUsd storageGb storageUsd totalUsd }
      optimizations { id impact icon title detail savingUsdMonthly }
      topJobs { fileName mode lane status submittedAt runHours machineHours gb computeUsd }
      topDownloads { fileName downloads shareDownloads cdnDownloads gb egressUsd }
    }
  }
`;

// Admin-only platform safety limits (0 = no limit).
export const ADMIN_SAFETY_LIMITS_QUERY = gql`
  query AdminSafetyLimits {
    adminSafetyLimits {
      maxFilesPointCloud
      maxFilesGaussian
      maxFiles2d
      maxFiles3d
      maxFilesDocument
      maxFilesOrthomosaic
      maxFilesVideo360
      maxSizePointCloudMb
      maxSizeGaussianMb
      maxSize2dMb
      maxSize3dMb
      maxSizeDocumentMb
      maxSizeOrthomosaicMb
      maxSizeVideo360Mb
      maxNotesPointCloud
      maxNotesGaussian
      maxAttachmentsPerNote
      maxAttachmentSizeMb
      maxGuestNotesPerHour
      maxSharedLinksPerProject
    }
  }
`;

export const ADMIN_BASEMAP_SETTINGS_QUERY = gql`
  query AdminBasemapSettings {
    adminBasemapSettings {
      esriApiKey
      googleApiKey
      terrainEnabled
      googleStatus
    }
  }
`;

// Tile providers (carrying the key) + terrain tiles the Scene may draw;
// share links pass their token instead of signing in.
export const BASEMAP_CONFIG_QUERY = gql`
  query BasemapConfig($shareToken: String) {
    basemapConfig(shareToken: $shareToken) {
      providers { id label url attribution maxZoom tileSize viewport }
      terrainUrl
    }
  }
`;

// What the Scene needs from the project itself: its grid and base map choices.
export const PROJECT_SCENE_QUERY = gql`
  query ProjectScene($id: ID!) {
    project(id: $id) {
      id
      latitude
      longitude
      epsg
      basemap { provider opacity terrain height position gridAdjust { dx dy rotation pivotX pivotY } }
      sitePlacement { lat lng heading originX originY originZ }
    }
  }
`;

export const ADMIN_ARCHIVE_SETTINGS_QUERY = gql`
  query AdminArchiveSettings {
    adminArchiveSettings {
      enabled
      delayedDownloadDays
      fullArchiveDays
      restoreKeepDays
    }
  }
`;

// Storage & delivery: mirrors of the bucket's and the distribution's setup.
export const ADMIN_DELIVERY_SETTINGS_QUERY = gql`
  query AdminDeliverySettings {
    adminDeliverySettings {
      intelligentTiering
      intelligentTieringSince
      downloadsViaCloudfront
      cloudfrontConfigured
      maxCdnDownloadGb
    }
  }
`;

export const ADMIN_EMAIL_SETTINGS_QUERY = gql`
  query AdminEmailSettings {
    adminEmailSettings {
      enabled
      provider
      fromName
      fromAddress
      appUrl
      sesRegion
      smtpHost
      smtpPort
      smtpSecure
      smtpUsername
      smtpPasswordSet
    }
  }
`;

export const BUCKET_CHECK_SELECTION = `
  running startedAt finishedAt objectsListed error
  result {
    objects totalGb recordedGb versionsListed noncurrentGb byClass { key gb usdMonthly } unattributedGb
    multipartUploads multipartGb multipartOldestAt
    orphanCount orphanGb deletableCount deletableGb missingCount missingGb differsCount
    factors { type label files rawGb derivedGb measured current }
    warnings { level message }
  }
  deletion { running startedAt finishedAt total done deleted deletedGb skipped failed error }
`;

// The last on-demand bucket check (kept in the backend's memory only).
export const ADMIN_BUCKET_CHECK_QUERY = gql`
  query AdminBucketCheck {
    adminBucketCheck {${BUCKET_CHECK_SELECTION}}
  }
`;

export const STORAGE_RECALC_SELECTION = `
  userId name state queuedAt startedAt finishedAt error folders totalGb originalGb convertedGb
  added changed removed differed missingFiles quotaFixed quotaChangeGb
`;

export const ADMIN_BUCKET_CHECK_FOLDERS_QUERY = gql`
  query AdminBucketCheckFolders($list: BucketCheckList!, $page: Int, $limit: Int, $search: String) {
    adminBucketCheckFolders(list: $list, page: $page, limit: $limit, search: $search) {
      folders { path gb recordedGb realGb belongsTo canDelete }
      total
      page
      limit
    }
  }
`;

export const ADMIN_STORAGE_RECALCS_QUERY = gql`
  query AdminStorageRecalcs($page: Int, $limit: Int) {
    adminStorageRecalcs(page: $page, limit: $limit) {
      recalcs {${STORAGE_RECALC_SELECTION}}
      total
      page
      limit
      active
    }
  }
`;

export const CALCULATOR_LINK_SELECTION = `id token label createdAt createdBy expiresAt revokedAt views lastViewedAt status`;

export const ADMIN_CALCULATOR_SHARE_LINKS_QUERY = gql`
  query AdminCalculatorShareLinks($page: Int, $limit: Int) {
    adminCalculatorShareLinks(page: $page, limit: $limit) {
      links { ${CALCULATOR_LINK_SELECTION} }
      total
      page
      limit
    }
  }
`;

export const MY_SUBUSERS_QUERY = gql`
  query MySubusers($page: Int, $limit: Int, $search: String) {
    mySubusers(page: $page, limit: $limit, search: $search) {
      subusers { id firstName lastName email status invitedAt lastLoginAt projectCount }
      total
      page
      limit
      seatsUsed
      maxSubusers
    }
  }
`;

export const SUBUSER_PROJECTS_QUERY = gql`
  query SubuserProjects($userId: ID!, $page: Int, $limit: Int, $search: String) {
    subuserProjects(userId: $userId, page: $page, limit: $limit, search: $search) {
      projects { projectId name role }
      total
      page
      limit
    }
  }
`;

export const SUBUSER_INVITE_QUERY = gql`
  query SubuserInvite($token: String!) {
    subuserInvite(token: $token) { email firstName lastName inviterName companyName }
  }
`;

export const PROJECT_MEMBER_CANDIDATES_QUERY = gql`
  query ProjectMemberCandidates($projectId: ID!, $page: Int, $limit: Int, $search: String) {
    projectMemberCandidates(projectId: $projectId, page: $page, limit: $limit, search: $search) {
      users { id firstName lastName email }
      total
      page
      limit
    }
  }
`;

export const PROJECT_MEMBERS_QUERY = gql`
  query ProjectMembers($projectId: ID!) {
    teamMembers(projectId: $projectId) {
      members { user { id firstName lastName email parentUser { id } } role joinedAt }
      total
    }
  }
`;
